import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";

export const PageRefreshModal = () => {
    const location = useLocation();
    const [showUpdate, setShowUpdate] = useState(false);
    const [currentVersion, setCurrentVersion] = useState<string | null>(
        localStorage.getItem("appVersion")
    );
    const trackedRoutes = ["/order", "/checkout"];
    const CHECK_TIMEOUT = 3 * 60 * 60 * 1000;
    const MIN_CHECK_INTERVAL = 3 * 60 * 1000;
    const MAX_CHECKS = 2;
    let activeRequests = 0;
    const isTrackedRoute = trackedRoutes.some(route => location.pathname.startsWith(route));

    const checkVersion = async () => {
        try {
            const lastCheckTime = parseInt(localStorage.getItem("lastVersionCheckTime") || "0", 10);
            const checkCount = parseInt(localStorage.getItem("versionCheckCount") || "0", 10);
            const now = Date.now();

            if (lastCheckTime && now - lastCheckTime < MIN_CHECK_INTERVAL) {
                return;
            }

            if (checkCount >= MAX_CHECKS && now - lastCheckTime < CHECK_TIMEOUT) {
                return;
            }

            const response = await fetch("/version.json", { cache: "no-store" });
            if (!response.ok) throw new Error("Failed to fetch version.json");

            const data = await response.json();

            if (currentVersion && currentVersion !== data.version) {
                setShowUpdate(true);
            } else {
                localStorage.setItem("appVersion", data.version);
                setCurrentVersion(data.version);

                localStorage.setItem("versionCheckCount", (checkCount + 1).toString());
                localStorage.setItem("lastVersionCheckTime", now.toString());
            }
        } catch (error) {
            console.error("Error checking version:", error);
        }
    };

    // Intercept fetch API calls to track API activity on specific routes
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
        if (isTrackedRoute) {
            activeRequests++;
        }

        try {
            return await originalFetch(...args);
        } finally {
            if (isTrackedRoute) {
                activeRequests--;
                if (activeRequests === 0) {
                    checkVersion();
                }
            }
        }
    };

    const handleVisibilityChange = () => {
        if (document.visibilityState === "visible") {
            checkVersion();
        }
    };

    useEffect(() => {
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRefresh = () => {
        localStorage.setItem("appVersion", "");
        window.location.reload();
    };

    return (
        <Modal show={showUpdate} centered backdrop="static">
            <Modal.Header>
                <Modal.Title>New Update Available</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                A new version of this application is available. Please refresh the page to get the latest updates.
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={handleRefresh}>Refresh</Button>
            </Modal.Footer>
        </Modal>
    );
};